import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { loadSiteConfig, trackPageView } from "./lib/analytics";
import { initTheme, useTheme } from "./lib/theme";
import { Seo } from "./components/Seo";
import { Toaster } from "sonner";

import { auth } from "./lib/api";
import Layout from "./components/Layout";
import { UpgradeGate } from "./components/UpgradeGate";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import FeaturesPage from "./pages/FeaturesPage";
import Campaigns from "./pages/Campaigns";
import CampaignBuilder from "./pages/CampaignBuilder";
import CreateCampaignChoice from "./pages/CreateCampaignChoice";
import CampaignAgent from "./pages/CampaignAgent";
import CTVCampaignBuilder from "./pages/CTVCampaignBuilder";
import Creatives from "./pages/Creatives";
import Audiences from "./pages/Audiences";
import GeoIntelligence from "./pages/GeoIntelligence";
import Footfall from "./pages/Footfall";
import FirstPartyData from "./pages/FirstPartyData";
import RadioInventory from "./pages/RadioInventory";
import AIAssistant from "./pages/AIAssistant";
import AICreativeStudio from "./pages/AICreativeStudio";
import AILandingPageBuilder from "./pages/AILandingPageBuilder";
import Political from "./pages/Political";
import Reports from "./pages/Reports";
import Billing from "./pages/Billing";
import LaunchApprovals from "./pages/LaunchApprovals";
import AdminPromotions from "./pages/AdminPromotions";
import SelectCampaign from "./pages/SelectCampaign";
import AdminTradeShows from "./pages/AdminTradeShows";
import PromoAnalytics from "./pages/PromoAnalytics";
import AdminSettings from "./pages/AdminSettings";
import AuditLogs from "./pages/AuditLogs";
import PoliticalOrganizations from "./pages/PoliticalOrganizations";
import TargetVoters from "./pages/TargetVoters";
import PoliticalData from "./pages/PoliticalData";
import PricingEngine from "./pages/PricingEngine";
import PlaybookEditor from "./pages/PlaybookEditor";
import Pricing from "./pages/Pricing";
import Welcome from "./pages/Welcome";
import WhatsNew from "./pages/WhatsNew";
import StackAdaptAdmin from "./pages/StackAdaptAdmin";
import CandidateDirectory from "./pages/CandidateDirectory";
import RacePage from "./pages/RacePage";
import PublicDirectory from "./pages/PublicDirectory";
import ScanLanding from "./pages/scan/ScanLanding";
import RaceShowcase from "./pages/scan/RaceShowcase";
import ResumeDemo from "./pages/scan/ResumeDemo";
import CTVPlanner from "./pages/CTVPlanner";
import ComplianceCenter from "./pages/ComplianceCenter";
import FundraisingDashboard from "./pages/fundraising/Dashboard";
import FundraisingCampaigns from "./pages/fundraising/Campaigns";
import LandingPages from "./pages/fundraising/LandingPages";
import Microsites from "./pages/fundraising/Microsites";
import Donors from "./pages/fundraising/Donors";
import Transactions from "./pages/fundraising/Transactions";
import SourceEconomics from "./pages/SourceEconomics";
import AgencyPortfolio from "./pages/AgencyPortfolio";
import AgencyTeam from "./pages/AgencyTeam";
import OnboardingChecklist from "./pages/OnboardingChecklist";
import VerifyCampaign from "./pages/VerifyCampaign";
import BrandLibrary from "./pages/BrandLibrary";
import AdStudioRefreshes from "./pages/AdStudioRefreshes";
import SmsKeywords from "./pages/SmsKeywords";
import Microsite, { MicrositeThanks } from "./pages/Microsite";
import ClaimPromo from "./pages/ClaimPromo";
import PhotoGallery from "./pages/intelligence/PhotoGallery";
import EntityOnboarding from "./pages/EntityOnboarding";
import RaceIntelligence from "./pages/RaceIntelligence";
import TeamDirectory from "./pages/TeamDirectory";
import AcceptInvite from "./pages/AcceptInvite";
import CampaignCalendar from "./pages/CampaignCalendar";
import OperationsHub from "./pages/OperationsHub";
import Portfolio from "./pages/Portfolio";
import ClaimsVault from "./pages/ClaimsVault";
import EventsPage from "./pages/EventsPage";
import PublicEvent from "./pages/PublicEvent";
import Volunteers from "./pages/Volunteers";
import AdLibraryPage from "./pages/AdLibraryPage";
import ElectionNight from "./pages/ElectionNight";
import QrCodes from "./pages/QrCodes";
import AskTotalReach from "./pages/AskTotalReach";
import { CTVReports } from "./pages/_Placeholders";
import MediaPlans from "./pages/MediaPlans";
import OtherTargeting from "./pages/OtherTargeting";
import HelpCenter from "./pages/HelpCenter";
import Optimize from "./pages/Optimize";
import WarRoom from "./pages/WarRoom";
import OpponentIntel from "./pages/OpponentIntel";
import Intelligence from "./pages/Intelligence";
import AdStudio from "./pages/AdStudio";
import AdStudioProject from "./pages/AdStudioProject";
import EmailCampaigns from "./pages/EmailCampaigns";
import DirectMail from "./pages/DirectMail";
import Journeys from "./pages/Journeys";
import PoliticalIntelligence from "./pages/PoliticalIntelligence";
import CandidateDashboard from "./pages/CandidateDashboard";
import SharedRace from "./pages/SharedRace";
import VolunteerPortal from "./pages/VolunteerPortal";
import VolunteerLeaderboard from "./pages/VolunteerLeaderboard";
import { ModeSwitch } from "./lib/uxMode";
import SimpleHome from "./pages/simple/SimpleHome";
import SimpleAdvertise from "./pages/simple/SimpleAdvertise";
import SimpleFundraise from "./pages/simple/SimpleFundraise";
import SimpleRace from "./pages/simple/SimpleRace";
import SimplePerformance from "./pages/simple/SimplePerformance";
import SimpleOperations from "./pages/simple/SimpleOperations";
import MoreTools from "./pages/simple/MoreTools";
import AutopilotSettings from "./pages/simple/AutopilotSettings";
import ClientPortal from "./pages/ClientPortal";
import QuoteRequest from "./pages/QuoteRequest";

const RouteTracker = () => {
  const location = useLocation();
  React.useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const user = auth.getUser();
  if (!user) return <Navigate to="/login" replace />;
  return <><Seo title="Workspace" noindex />{children}</>;
};

const RootGate = () => {
  const user = auth.getUser();
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
};

const MICROSITE_ROOT = "totalreach.us";
const RESERVED_SUBDOMAINS = ["www", "app", "api", "mg", "email", "mail", "admin"];

function detectMicrositeSlug() {
  const host = window.location.hostname;
  if (!host.endsWith(`.${MICROSITE_ROOT}`)) return null;
  const sub = host.slice(0, -(MICROSITE_ROOT.length + 1));
  if (!sub || sub.includes(".") || RESERVED_SUBDOMAINS.includes(sub)) return null;
  return sub;
}

function App() {
  const micrositeSlug = detectMicrositeSlug();
  React.useEffect(() => { loadSiteConfig(); return initTheme(); }, []);
  const { theme } = useTheme();
  if (micrositeSlug) {
    return (
      <div className="App min-h-screen bg-app text-white">
        <Toaster theme={theme} position="bottom-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/thanks" element={<MicrositeThanks slugOverride={micrositeSlug} />} />
            <Route path="*" element={<Microsite slugOverride={micrositeSlug} />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App min-h-screen bg-app text-white">
      <Toaster theme={theme} position="bottom-right" />
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login mode="signup" />} />
          <Route path="/request-demo" element={<Login mode="demo" />} />
          <Route path="/reset-password" element={<Login mode="reset" />} />
          <Route path="/directory" element={<PublicDirectory />} />
          <Route path="/scan" element={<ScanLanding />} />
          <Route path="/scan/race/:raceId" element={<RaceShowcase />} />
          <Route path="/scan/r/:code" element={<ResumeDemo />} />
          <Route path="/directory/race/:raceId" element={<RacePage publicMode />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/whats-new" element={<WhatsNew />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/claim/:code" element={<ClaimPromo />} />
          <Route path="/m/:slug" element={<Microsite />} />
          <Route path="/m/:slug/thanks" element={<MicrositeThanks />} />
          <Route path="/race/:token" element={<SharedRace />} />
          <Route path="/client/:token" element={<ClientPortal />} />
          <Route path="/quote/:token" element={<QuoteRequest />} />
          <Route path="/volunteer/:token" element={<VolunteerPortal />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/onboarding/political" element={<ProtectedRoute><EntityOnboarding /></ProtectedRoute>} />
          <Route path="/select-campaign" element={<ProtectedRoute><SelectCampaign /></ProtectedRoute>} />
          <Route path="/accept-invite/:token" element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
          <Route path="/e/:slug" element={<PublicEvent />} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<ModeSwitch simple={<SimpleHome />} pro={<Home />} />} />
            <Route path="/dashboard/advanced" element={<Home />} />
            <Route path="/advertise" element={<SimpleAdvertise />} />
            <Route path="/fundraise" element={<SimpleFundraise />} />
            <Route path="/race" element={<SimpleRace />} />
            <Route path="/performance" element={<SimplePerformance />} />
            <Route path="/more-tools" element={<MoreTools />} />
            <Route path="/autopilot" element={<AutopilotSettings />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/ask-totalreach" element={<UpgradeGate feature="ai" title="Ask TotalReach — AI Chief of Staff"><AskTotalReach /></UpgradeGate>} />
            <Route path="/war-room" element={<UpgradeGate feature="war_room" title="The Campaign War Room"><WarRoom /></UpgradeGate>} />

            {/* Advertise */}
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/new" element={<CreateCampaignChoice />} />
            <Route path="/campaigns/new/pro" element={<CampaignBuilder />} />
            <Route path="/campaign-agent/:sid" element={<UpgradeGate feature="ai" title="The AI Campaign Agent"><CampaignAgent /></UpgradeGate>} />
            <Route path="/campaigns/:id" element={<CampaignBuilder />} />
            <Route path="/campaigns/ctv/new" element={<CTVCampaignBuilder />} />
            <Route path="/planner/ctv" element={<CTVPlanner />} />
            <Route path="/media-plans" element={<MediaPlans />} />
            <Route path="/email-campaigns" element={<UpgradeGate feature="email" title="Email Campaigns"><EmailCampaigns /></UpgradeGate>} />
            <Route path="/direct-mail" element={<UpgradeGate feature="direct_mail" title="Programmatic Direct Mail"><DirectMail /></UpgradeGate>} />
            <Route path="/journeys" element={<UpgradeGate feature="journeys" title="Omnichannel Journeys"><Journeys /></UpgradeGate>} />

            {/* Target */}
            <Route path="/target-voters" element={<TargetVoters />} />
            <Route path="/political-data" element={<PoliticalData />} />
            <Route path="/audiences" element={<Audiences />} />
            <Route path="/first-party" element={<UpgradeGate feature="voter_files" title="Voter File Onboarding"><FirstPartyData /></UpgradeGate>} />
            <Route path="/geo" element={<GeoIntelligence />} />
            <Route path="/other-targeting" element={<OtherTargeting />} />

            {/* Fundraise */}
            <Route path="/fundraising" element={<UpgradeGate feature="fundraising" title="The Fundraising Suite"><FundraisingDashboard /></UpgradeGate>} />
            <Route path="/fundraising/campaigns" element={<UpgradeGate feature="fundraising" title="Fundraising Campaigns"><FundraisingCampaigns /></UpgradeGate>} />
            <Route path="/fundraising/campaigns/:id" element={<UpgradeGate feature="fundraising" title="Fundraising Campaigns"><FundraisingCampaigns /></UpgradeGate>} />
            <Route path="/fundraising/landing-pages" element={<UpgradeGate feature="fundraising" title="Landing Pages"><LandingPages /></UpgradeGate>} />
            <Route path="/fundraising/microsites" element={<UpgradeGate feature="fundraising" title="Microsites"><Microsites /></UpgradeGate>} />
            <Route path="/fundraising/donors" element={<UpgradeGate feature="fundraising" title="The Donor CRM"><Donors /></UpgradeGate>} />
            <Route path="/fundraising/transactions" element={<UpgradeGate feature="fundraising" title="Transactions"><Transactions /></UpgradeGate>} />
            <Route path="/fundraising/economics" element={<UpgradeGate feature="fundraising" title="Fundraising Source Economics"><SourceEconomics /></UpgradeGate>} />

            {/* Create */}
            <Route path="/ad-studio" element={<UpgradeGate feature="ai" title="The Political Ad Studio"><AdStudio /></UpgradeGate>} />
            <Route path="/ad-studio/:id" element={<UpgradeGate feature="ai" title="The Political Ad Studio"><AdStudioProject /></UpgradeGate>} />
            <Route path="/ai-assistant" element={<UpgradeGate feature="ai" title="The AI Campaign Assistant"><AIAssistant /></UpgradeGate>} />
            <Route path="/creative-studio" element={<UpgradeGate feature="ai" title="The AI Creative Studio"><AICreativeStudio /></UpgradeGate>} />
            <Route path="/ai-landing-page" element={<UpgradeGate feature="ai" title="The AI Landing Page Builder"><AILandingPageBuilder /></UpgradeGate>} />
            <Route path="/creatives" element={<Creatives />} />

            {/* Intelligence */}
            <Route path="/political-intelligence" element={<UpgradeGate feature="intelligence" title="Political Intelligence"><PoliticalIntelligence /></UpgradeGate>} />
            <Route path="/race-intelligence" element={<RaceIntelligence />} />
            <Route path="/team" element={<TeamDirectory />} />
            <Route path="/calendar" element={<CampaignCalendar />} />
            <Route path="/operations" element={<ModeSwitch simple={<SimpleOperations />} pro={<OperationsHub />} />} />
            <Route path="/operations/advanced" element={<OperationsHub />} />
            <Route path="/approvals/launch" element={<LaunchApprovals />} />
            <Route path="/admin/promotions" element={<AdminPromotions />} />
            <Route path="/admin/promotions/events" element={<AdminTradeShows />} />
            <Route path="/admin/promotions/analytics" element={<PromoAnalytics />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/agency" element={<UpgradeGate feature="agency" title="Agency Portfolio"><AgencyPortfolio /></UpgradeGate>} />
            <Route path="/agency/team" element={<UpgradeGate feature="agency" title="Agency Team"><AgencyTeam /></UpgradeGate>} />
            <Route path="/onboarding/checklist" element={<OnboardingChecklist />} />
            <Route path="/verify-campaign" element={<VerifyCampaign />} />
            <Route path="/brand-library" element={<BrandLibrary />} />
            <Route path="/ad-studio/refreshes" element={<UpgradeGate feature="ai" title="Ad Studio Weekly Refresh"><AdStudioRefreshes /></UpgradeGate>} />
            <Route path="/fundraising/sms" element={<UpgradeGate feature="fundraising" title="Text-to-Give SMS"><SmsKeywords /></UpgradeGate>} />
            <Route path="/claims-vault" element={<ClaimsVault />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/volunteers" element={<Volunteers />} />
            <Route path="/volunteers/leaderboard" element={<VolunteerLeaderboard />} />
            <Route path="/ad-library" element={<AdLibraryPage />} />
            <Route path="/election-night" element={<ElectionNight />} />
            <Route path="/qr-codes" element={<QrCodes />} />
            <Route path="/qr" element={<QrCodes />} />
            <Route path="/political-intelligence/:id" element={<UpgradeGate feature="intelligence" title="Political Intelligence"><CandidateDashboard /></UpgradeGate>} />
            <Route path="/political-intelligence/:id/photos" element={<UpgradeGate feature="intelligence" title="Political Intelligence"><PhotoGallery /></UpgradeGate>} />
            <Route path="/midterms" element={<CandidateDirectory />} />
            <Route path="/midterms/race/:raceId" element={<RacePage />} />
            <Route path="/intelligence" element={<Intelligence />} />
            <Route path="/political" element={<Political />} />
            <Route path="/opponent" element={<OpponentIntel />} />

            {/* Measure */}
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/ctv" element={<CTVReports />} />
            <Route path="/optimize" element={<Optimize />} />

            {/* Compliance */}
            <Route path="/compliance" element={<UpgradeGate feature="compliance" title="The Compliance Center"><ComplianceCenter /></UpgradeGate>} />
            <Route path="/audit" element={<AuditLogs />} />

            {/* Account */}
            <Route path="/organizations" element={<PoliticalOrganizations />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/pricing/admin" element={<PricingEngine />} />
            <Route path="/admin/playbooks" element={<PlaybookEditor />} />
            <Route path="/ad-platform" element={<StackAdaptAdmin />} />
            <Route path="/admin" element={<AdminSettings />} />
            <Route path="/help" element={<HelpCenter />} />

            {/* Legacy — keep for regression */}
            <Route path="/radio" element={<RadioInventory />} />
            <Route path="/footfall" element={<Footfall />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
